/**
 * ComparisonTray — a floating tray (bottom-right, all pages) that holds the
 * claims/findings the user picked via "+ add to comparison", and sends them to
 * the Claim Adjudicator. The verdicts render as an answer in the RAG/Ask panel.
 *
 * Lives in the app shell (Layout) so the selection is reachable from any tab.
 */
import React, { useState } from 'react';
import { Box, Stack, Typography, IconButton, Button, Collapse, Tooltip } from '@mui/material';
import { Balance, ExpandLess, ExpandMore, DeleteOutline, Close } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useComparison } from '../../contexts/ComparisonContext';
import { navigateToRagWithComparison } from '../../lib/ragPrefill';
import { mvkgTokens, accentSurface } from '../../theme/mvkgTokens';

export default function ComparisonTray() {
  const { items, removeItem, clear, max } = useComparison();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (!items.length) return null;

  const go = () => {
    navigateToRagWithComparison(navigate, items);
    clear();
    setOpen(false);
  };

  return (
    <Box
      sx={{
        position: 'fixed', bottom: 20, right: 20, zIndex: 1300,
        width: 340, maxWidth: '92vw',
        bgcolor: 'rgba(255,255,255,0.97)',
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: `${mvkgTokens.radius?.md || 14}px`,
        boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row" alignItems="center" spacing={1}
        sx={{ px: 1.5, py: 1, cursor: 'pointer', bgcolor: accentSurface.brand.bg }}
        onClick={() => setOpen((o) => !o)}
      >
        <Balance sx={{ fontSize: 18, color: accentSurface.brand.fg }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1, color: accentSurface.brand.fg }}>
          Comparison · {items.length}/{max}
        </Typography>
        <Tooltip title="Clear">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); clear(); }}>
            <DeleteOutline sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <IconButton size="small">
          {open ? <ExpandMore sx={{ fontSize: 18 }} /> : <ExpandLess sx={{ fontSize: 18 }} />}
        </IconButton>
      </Stack>

      <Collapse in={open}>
        <Box sx={{ maxHeight: 240, overflowY: 'auto', px: 1.5, py: 1 }}>
          {items.map((it, idx) => (
            <Stack
              key={idx} direction="row" spacing={0.75} alignItems="flex-start"
              sx={{ py: 0.5, borderBottom: idx < items.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, color: accentSurface.brand.fg, mt: '2px' }}>
                A{idx + 1}
              </Typography>
              <Typography variant="caption" sx={{ flex: 1, lineHeight: 1.35 }}>
                {it.text.length > 120 ? `${it.text.slice(0, 120)}…` : it.text}
                {it.article_id != null && (
                  <Box component="span" sx={{ color: 'text.secondary', ml: 0.5 }}>#{it.article_id}</Box>
                )}
              </Typography>
              <IconButton size="small" onClick={() => removeItem(idx)} sx={{ p: 0.25 }}>
                <Close sx={{ fontSize: 14 }} />
              </IconButton>
            </Stack>
          ))}
        </Box>
      </Collapse>

      <Box sx={{ p: 1.25, pt: 1 }}>
        <Button
          fullWidth variant="contained" size="small" startIcon={<Balance sx={{ fontSize: 16 }} />}
          onClick={go} disabled={items.length < 1}
          sx={{ fontWeight: 700, textTransform: 'none', borderRadius: 1.5 }}
        >
          Compare {items.length} {items.length === 1 ? 'statement' : 'statements'}
        </Button>
      </Box>
    </Box>
  );
}
